import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { API, ClientConnectionEstablishmentRequestDTO, Pagination } from '../../../services/api/api';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '../../../services/error.service';
import { ConnectClientModalID } from './create/create-client-connection.component';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'delete';

@Component({
  selector: 'page-client-connections',
  templateUrl: './client-connections.component.html',
  styleUrls: ['./client-connections.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientConnectionsComponent extends RequestClass implements AfterViewInit {
  private readonly api = inject(API);
  private readonly route = inject(ActivatedRoute);
  private readonly toaster = inject(ToasterService);
  private readonly errorService = inject(ErrorService);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  ConnectClientModalID = ConnectClientModalID;

  // table wrapper variables
  public connectedClients: ClientConnectionEstablishmentRequestDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  ngAfterViewInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params?.token) {
        this.openConnectModal(params.token);
      }
    });
  }

  public refreshFromServer(): void {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .shared_ClientConnection_retrieveClientConnectionEstablishmentRequests(
        {
          connected: true,
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe(connected => {
        this.connectedClients = this.buildSplitButtons(connected);
      });
    this.subs.push(sub);
  }

  openConnectModal(token: string = null) {
    this.modalService
      .modal(
        {
          title: 'Mandant verknüpfen',
          modalOptions: { windowClass: 'M' },
        },
        {
          templateId: ConnectClientModalID,
        },
        {
          token,
        }
      )
      .then(() => {})
      .catch(() => {});
  }

  onClientConnected(): void {
    this.modalService.close(ConnectClientModalID);
  }

  removeClientConnection(client: ClientConnectionEstablishmentRequestDTO): void {
    if (client) {
      this.modalService
        .confirm({
          title: this.translate.instant('lang.clientManagement.connection.delete'),
          message: this.translate.instant('lang.clientManagement.connection.delete.infoText', { clientName: client.requested.name }),
          yes: this.translate.instant('lang.clientManagement.connection.deleteAction'),
          no: this.translate.instant('lang.globals.cancel'),
        })
        .then((result: boolean) => {
          if (result) {
            this.api
              .shared_ClientConnection_withdrawClientConnectionEstablishmentRequest(client.requested.id)
              .pipe(take(1))
              .subscribe({
                next: () => {
                  this.refreshFromServer();
                  this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.clientManagement.connection.deleted'));
                },
                error: error => {
                  this.errorService.errorHandler(error);
                },
              });
          }
        });
    }
  }

  buildSplitButtons<T>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'trash',
        iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
        action: 'delete',
        tooltip: 'lang.clientManagement.connection.delete',
      });
    });
    return result;
  }

  resolveSplitButtonAction(client: ClientConnectionEstablishmentRequestDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.removeClientConnection(client);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
